import { useState, useEffect, useCallback, useMemo } from 'react';

// Define defaults outside component to avoid recreating on every render
export const POSITION_FILTER_DEFAULTS = {
  minPositionSize: 1000000,   // $1M (reduces confusion with inaccurate openedAt)
  symbols: [],                // Empty = all symbols
  side: 'all',                // 'all', 'long', or 'short'
  userSearch: ''              // Empty = no user filter
};

const DEFAULTS = POSITION_FILTER_DEFAULTS;

/**
 * Custom hook for Hyperliquid position filters management
 * Following useFilters.js pattern
 */
export function usePositionFilters() {

  const [filters, setFilters] = useState(() => {
    // Load from localStorage or use defaults
    try {
      const saved = localStorage.getItem('hlPositionFilterPreferences');
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          minPositionSize: parsed.minPositionSize || DEFAULTS.minPositionSize,
          symbols: parsed.symbols || DEFAULTS.symbols,
          side: parsed.side || DEFAULTS.side,
          userSearch: parsed.userSearch || DEFAULTS.userSearch
        };
      }
    } catch (error) {
      console.error('❌ Error loading position filters from localStorage:', error);
    }

    return DEFAULTS;
  });

  // Save filters to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('hlPositionFilterPreferences', JSON.stringify(filters));
    } catch (error) {
      console.error('Error saving position filters to localStorage:', error);
    }
  }, [filters]);

  // Computed filter state
  const hasActiveFilters = useMemo(() => {
    return (
      filters.minPositionSize !== DEFAULTS.minPositionSize ||
      filters.side !== DEFAULTS.side ||
      (filters.symbols && filters.symbols.length > 0) ||
      (filters.userSearch && filters.userSearch.trim().length > 0)
    );
  }, [filters]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.minPositionSize !== DEFAULTS.minPositionSize) count++;
    if (filters.side !== DEFAULTS.side) count++;
    if (filters.symbols && filters.symbols.length > 0) count++;
    if (filters.userSearch && filters.userSearch.trim().length > 0) count++;
    return count;
  }, [filters]);

  // Update minimum position size
  const setMinPositionSize = useCallback((value) => {
    setFilters(prev => ({
      ...prev,
      minPositionSize: Math.max(0, value)
    }));
  }, []);

  // Update symbols filter
  const setSymbols = useCallback((symbols) => {
    setFilters(prev => ({
      ...prev,
      symbols: Array.isArray(symbols) ? symbols : []
    }));
  }, []);

  // Update side filter
  const setSide = useCallback((side) => {
    const validSides = ['all', 'long', 'short'];
    if (validSides.includes(side)) {
      setFilters(prev => ({
        ...prev,
        side
      }));
    }
  }, []);

  // Update user search
  const setUserSearch = useCallback((search) => {
    setFilters(prev => ({
      ...prev,
      userSearch: typeof search === 'string' ? search : ''
    }));
  }, []);

  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    setFilters(DEFAULTS);
  }, []);

  // Format dollar amount for display
  const formatDollarAmount = useCallback((amount) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    } else {
      return `$${amount}`;
    }
  }, []);

  return {
    filters,
    setMinPositionSize,
    setSymbols,
    setSide,
    setUserSearch,
    resetToDefaults,
    formatDollarAmount,
    hasActiveFilters,
    activeFilterCount
  };
}
